<template>
  <div data-testid="shop-billing-address-form" class="px-8 pb-8">
    <h3 v-if="props.showHeading" class="mb-4 pt-8 txt-headline-s">{{ t('FestivalShop.checkout.billingAddress') }}</h3>

    <div class="">
      <template v-if="!props.isLocked">
        <div class="grid grid-cols-2 gap-4">
          <SelectInput
            v-model="formData.salutation"
            data-testid="shop-billing-salutation"
            name="salutation"
            :label="t('FestivalShop.checkout.salutation')"
            :error="getValidationErrors(v$.salutation)"
            @change="v$.salutation.$validate"
          >
            <option value=""></option>
            <option v-for="option in salutationOptions" :key="option.enum" :value="option.enum">{{ option.name }}</option>
          </SelectInput>
          <SelectInput v-model="formData.title" data-testid="shop-billing-title" name="title" :label="t('FestivalShop.checkout.title')">
            <option value=""></option>
            <option v-for="option in titleOptions" :key="option.value" :value="option.value">
              {{ option.text }}
            </option>
          </SelectInput>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <TextInput
            v-model="formData.firstName"
            data-testid="shop-billing-first-name"
            name="firstName"
            :label="t('FestivalShop.checkout.firstName')"
            :error="getValidationErrors(v$.firstName)"
            @update:model-value="v$.firstName.$validate"
          />
          <TextInput
            v-model="formData.lastName"
            data-testid="shop-billing-last-name"
            name="lastName"
            :label="t('FestivalShop.checkout.lastName')"
            :error="getValidationErrors(v$.lastName)"
            @update:model-value="v$.lastName.$validate"
          />
        </div>

        <TextInput
          v-model="formData.email"
          data-testid="shop-billing-email"
          name="email"
          type="email"
          :label="t('FestivalShop.checkout.email')"
          :error="getValidationErrors(v$.email)"
          @update:model-value="v$.email.$validate"
        />

        <SelectInput
          v-model="formData.country"
          data-testid="shop-billing-country"
          name="country"
          :label="t('FestivalShop.checkout.country')"
          :error="getValidationErrors(v$.country)"
          @change="v$.country.$validate"
        >
          <option v-for="{ code, translation } in countries" :key="code" :value="code">{{ translation }}</option>
        </SelectInput>

        <TextInput
          v-model="formData.company"
          data-testid="shop-billing-company"
          name="company"
          :label="t('FestivalShop.checkout.company')"
          :error="getValidationErrors(v$.company)"
          @update:model-value="v$.company.$validate"
        />

        <TextInput
          v-model="formData.vatNumber"
          data-testid="shop-billing-vat-number"
          name="vatNumber"
          :optional="true"
          :label="t('FestivalShop.checkout.vatNumber')"
        />

        <TextInput
          v-model="formData.customerReference"
          data-testid="shop-billing-customer-reference"
          name="customerReference"
          :optional="true"
          :label="t('FestivalShop.checkout.orderPoNumber')"
        />

        <div class="grid grid-cols-3 gap-4">
          <TextInput
            v-model="formData.zip"
            data-testid="shop-billing-zip"
            name="zip"
            :label="t('FestivalShop.checkout.zipCode')"
            :error="getValidationErrors(v$.zip)"
            @update:model-value="v$.zip.$validate"
          />
          <div class="col-span-2">
            <TextInput
              v-model="formData.city"
              data-testid="shop-billing-city"
              name="city"
              :label="t('FestivalShop.checkout.city')"
              :error="getValidationErrors(v$.city)"
              @update:model-value="v$.city.$validate"
            />
          </div>
        </div>

        <div class="grid grid-cols-4 gap-4">
          <div class="col-span-3">
            <TextInput
              v-model="formData.street"
              data-testid="shop-billing-street"
              name="street"
              :label="t('FestivalShop.checkout.street')"
              :error="getValidationErrors(v$.street)"
              @update:model-value="v$.street.$validate"
            />
          </div>
          <TextInput
            v-model="formData.number"
            data-testid="shop-billing-number"
            name="number"
            :label="t('FestivalShop.checkout.no')"
            :error="getValidationErrors(v$.number)"
            @update:model-value="v$.number.$validate"
          />
        </div>

        <TextInput
          v-model="formData.additionalAddress"
          data-testid="shop-billing-additional-address"
          name="additionalAddress"
          :optional="true"
          :label="t('FestivalShop.checkout.addressAddition')"
        />
      </template>

      <div v-if="showBoothConfirmation" data-testid="shop-billing-booth-confirmation" class="flex items-start gap-2 rounded-sm p-4">
        <CheckBox v-model="formData.confirmBooth" data-testid="shop-billing-confirm-booth" />
        <label class="cursor-pointer txt-body-m" @click="formData.confirmBooth = !formData.confirmBooth">
          {{
            t('FestivalShop.checkout.confirmOrder', {
              boothName,
              companyName: props.order?.partnerCompany?.name,
            })
          }}
        </label>
      </div>

      <div data-testid="shop-billing-accept-terms" class="flex items-start gap-2 rounded-sm p-4">
        <CheckBox v-model="formData.acceptTerms" data-testid="shop-billing-accept-terms" />
        <label class="cursor-pointer txt-body-m" @click="formData.acceptTerms = !formData.acceptTerms">
          <span v-html="t('FestivalShop.checkout.acceptTerms')" />
        </label>
      </div>

      <button
        data-testid="shop-billing-submit-btn"
        class="btn-primary-color-l inline-flex w-full items-center justify-center gap-2"
        :disabled="!isFormValid || isSubmitting"
        @click="handleSubmit"
      >
        <span>{{ t('FestivalShop.checkout.confirmPurchase') }}</span>
        <ChevronRight class="lucide-icon-m shrink-0" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { SelectInput, TextInput } from '@ramp106/omrjs-core-ui/deprecated'
import { ChevronRight } from '@ramp106/omrjs-core-ui/icons'
import { useVuelidate } from '@vuelidate/core'
import { storeToRefs } from 'pinia'
import { computed, inject, reactive, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { useCountriesAndNationalitiesData } from '@/composables/useCountriesAndNationalitiesData'
import { FestivalShopCartKey } from '@/composables/useFestivalShopCart'
import { useFormData } from '@/composables/useFormData'
import { festivalShopConfig } from '@/config/festivalShop'
import { getValidationErrors } from '@/helpers/validationHelper'
import { required, email as emailValidator } from '@/services/validators'
import { useMeStore } from '@/stores'
import CheckBox from '@/ui/CheckBox.vue'
import type { ExtrasOrderNode } from '@/views/FestivalShop'

const { t } = useI18n()
const { salutationOptions, titleOptions } = useFormData()
const { countries } = useCountriesAndNationalitiesData()

const festivalShopCart = inject(FestivalShopCartKey)
if (!festivalShopCart) {
  throw new Error('FestivalShopCart not provided')
}

const props = withDefaults(
  defineProps<{
    order?: ExtrasOrderNode
    isSubmitting: boolean
    showHeading?: boolean
    isLocked?: boolean
  }>(),
  {
    showHeading: true,
    isLocked: false,
    order: undefined,
  },
)

const emit = defineEmits<{
  submit: [formData: FormData]
}>()

export interface FormData {
  salutation: string
  title: string
  firstName: string
  lastName: string
  email: string
  country: string
  company: string
  vatNumber: string
  customerReference: string
  zip: string
  city: string
  street: string
  number: string
  additionalAddress: string
  confirmBooth: boolean
  acceptTerms: boolean
}

const meStore = useMeStore()
const { me } = storeToRefs(meStore)

const formData = reactive<FormData>({
  salutation: '',
  title: '',
  firstName: '',
  lastName: '',
  email: '',
  country: '',
  company: '',
  vatNumber: '',
  customerReference: '',
  zip: '',
  city: '',
  street: '',
  number: '',
  additionalAddress: '',
  confirmBooth: false,
  acceptTerms: false,
})

const applyOrder = (order?: ExtrasOrderNode) => {
  formData.salutation = order?.salutation || me.value?.salutation || ''
  formData.title = order?.title || ''
  formData.firstName = order?.firstName || me.value?.firstName || ''
  formData.lastName = order?.lastName || me.value?.lastName || ''
  formData.email = order?.email || me.value?.email || ''
  formData.country = order?.country || me.value?.partnerCompany?.countryCode || ''
  formData.company = order?.company || me.value?.partnerCompany?.displayName || me.value?.partnerCompany?.name || ''
  formData.vatNumber = order?.vatNumber || ''
  formData.customerReference = order?.customerReference || ''
  formData.zip = order?.zip || me.value?.partnerCompany?.zipcode || ''
  formData.city = order?.city || me.value?.partnerCompany?.city || ''
  formData.street = order?.street || me.value?.partnerCompany?.street || ''
  formData.number = order?.number || ''
  formData.additionalAddress = order?.additionalAddress || ''
  formData.confirmBooth = false
  formData.acceptTerms = false
}

watch(
  () => props.order,
  (order) => {
    applyOrder(order)
  },
  { immediate: true },
)

const boothName = computed(() => {
  if (!props.isLocked) return null
  const kiosk = props.order?.kiosk
  if (kiosk?.__typename === 'Booth') {
    return kiosk.boothNumber ?? kiosk.name
  }
  return null
})
const showBoothConfirmation = computed(() => festivalShopConfig.showBoothConfirmationCheckbox && !!boothName.value)

const rules = {
  salutation: { required },
  firstName: { required },
  lastName: { required },
  email: { required, emailValidator },
  country: { required },
  company: { required },
  zip: { required },
  city: { required },
  street: { required },
  number: { required },
}

const v$ = useVuelidate(rules, formData)

const isFormValid = computed(() => {
  const termsAccepted = formData.acceptTerms
  const boothConfirmed = !showBoothConfirmation.value || formData.confirmBooth
  return !v$.value.$invalid && termsAccepted && boothConfirmed
})

const handleSubmit = async () => {
  const isValid = await v$.value.$validate()
  if (!isValid || !isFormValid.value) return
  emit('submit', formData)
}
</script>
<style scoped>
@reference '@ramp106/omrjs-config-tailwind/styles/index.css';
:deep(select) {
  @apply h-10!;
}
</style>
